import { createReducer } from '@reduxjs/toolkit';
import {
	CustomerDomainEngagementExpectedError,
	FastshiftUserDetailsExpectedError,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { DEFAULT_CONTROLS_PAYLOAD } from '@/common/fastshift-table/src/constants';

import * as StateActions from './actions';
import { FASTSHIFT_LOCALSTORAGE_KEYS, RESERVED_ORG_IDS, TABLE_IDS } from './constants';
import * as StateTypes from './types';
import { captureErrorWithContext, getRunbookStepActionKey, getUserDomain } from './utils';

export type FastShiftState = {
	userDetails: StateTypes.UserDetails;
	userAccess: StateTypes.UserAccess;
	selectedOrg: StateTypes.Org | null;
	orgs: StateTypes.Orgs;
	migrationPlanTotals: StateTypes.MigrationPlanTotals;
	l1Assessments: StateTypes.L1Assessments;
	instances: StateTypes.InstancesRequest;
	// M2 state
	customerDomains: StateTypes.FastshiftCustomerDomainsState;
	customerDomain: StateTypes.CustomerDomainDetails;
	customerDomainEngagement: StateTypes.CustomerDomainEngagement;
	selectedDomainOrg: StateTypes.CustomerDomainOrg | null;
	topSupportRequests: StateTypes.TopSupportRequests;
	supportRequests: StateTypes.SupportRequests;
	runbooks: StateTypes.Runbooks;
	pageStateCache: StateTypes.PageStateCache;
	// Note only Customer role needs to consent; for other roles, false is expected.
	portalUserConsent: StateTypes.PortalUserConsent;
};

const setRunbookStepActionPending = (state: FastShiftState, runbookId: string, stepId: string) => {
	state.runbooks.stepActionsByStepKey[getRunbookStepActionKey({ runbookId, stepId })] = {
		pending: true,
		error: null,
	};
};

const setRunbookStepActionSettled = (
	state: FastShiftState,
	runbookId: string,
	stepId: string,
	error: string | null,
) => {
	state.runbooks.stepActionsByStepKey[getRunbookStepActionKey({ runbookId, stepId })] = {
		pending: false,
		error,
	};
};

const updateCachedRunbookStep = (
	state: FastShiftState,
	runbookId: string,
	stepId: string,
	stepPatch: Partial<NonNullable<StateTypes.RunbookDetail['data']>['steps'][number]>,
) => {
	const runbook = state.runbooks.detailsByRunbookId[runbookId]?.data;

	if (!runbook) {
		return;
	}

	const step = runbook.steps.find((runbookStep) => {
		return runbookStep.stepId === stepId;
	});

	if (!step) {
		return;
	}

	Object.assign(step, stepPatch);
};

// States here should be post-mapping, meaning that they are not necessarily the same as the response from the API
// but rather the ideal shape of the data that we want to work with. Mapping can be done in the reducer.
export const initialState: FastShiftState = {
	userDetails: {
		pending: false,
		data: null,
		error: null,
	},
	userAccess: {
		pending: false,
		data: null,
		error: null,
	},
	portalUserConsent: {
		pending: false,
		data: null,
		error: null,
	},
	selectedOrg: null,
	selectedDomainOrg: null,
	orgs: {
		pending: false,
		data: [],
		error: null,
	},
	migrationPlanTotals: {
		pending: false,
		data: {
			totalPlansCompleted: 0,
			totalPlansUnsuccessful: 0,
			totalPlansRunning: 0,
		},
		error: null,
	},
	l1Assessments: {
		pending: false,
		data: [],
		error: null,
	},
	instances: {
		pending: false,
		data: { instances: [], totalCount: 0 },
		error: null,
	},
	customerDomain: {
		pending: false,
		error: null,
		data: null,
	},
	customerDomainEngagement: {
		pending: false,
		error: null,
		data: null,
	},

	// states for partner and fastshift team roles only:
	customerDomains: {
		pending: false,
		error: null,
		data: null,
	},
	topSupportRequests: {
		pending: false,
		error: null,
		data: null,
	},
	supportRequests: {
		pending: false,
		error: null,
		data: null,
	},
	runbooks: {
		summariesBySupportTicketId: {},
		detailsByRunbookId: {},
		stepActionsByStepKey: {},
	},
	pageStateCache: {
		[TABLE_IDS.SELECTION]: {
			...DEFAULT_CONTROLS_PAYLOAD,
			filters: {
				...DEFAULT_CONTROLS_PAYLOAD.filters,
				status: null,
			},
		},
		[TABLE_IDS.MIGRATION_TICKETS]: DEFAULT_CONTROLS_PAYLOAD,
	},
};

// collects tickets where user is not an org admin for related site
export const noOrg: StateTypes.Org = {
	id: RESERVED_ORG_IDS.NO_ORG,
	isAdmin: true,
	name: 'No Org', // do not display this name to customers
};

export const reducer = (state: FastShiftState = initialState) =>
	createReducer(state, (builder) => {
		builder
			.addCase(StateActions.setSelectedOrg, (state, { payload }) => {
				state.selectedOrg = payload;
				// save selected org to localstorage
				localStorage.setItem(
					FASTSHIFT_LOCALSTORAGE_KEYS.SELECTED_ORG_ID,
					JSON.stringify(payload.id),
				);
			})
			.addCase(StateActions.setSelectedDomainOrg, (state, { payload }) => {
				state.selectedDomainOrg = payload;

				// Clear support requests when org changes to prevent stale data
				state.supportRequests.pending = false;
				state.supportRequests.error = null;
				state.supportRequests.data = null;
				state.supportRequests.lastFetchParams = undefined;

				// Clear top support requests when org changes to prevent stale data
				state.topSupportRequests.pending = false;
				state.topSupportRequests.error = null;
				state.topSupportRequests.data = null;
				state.topSupportRequests.lastFetchParams = undefined;

				// Clear runbooks when org changes to prevent stale Marathon data
				state.runbooks = initialState.runbooks;
			})
			.addCase(StateActions.getAccountOrgs.fulfilled, (state, { payload }) => {
				state.orgs.pending = false;
				// filter out orgs where user is not an admin
				const orgsList =
					payload?.reduce((acc: StateTypes.Org[], { id, isAdmin, name }) => {
						if (isAdmin) {
							acc.push({ id, isAdmin, name });
						}
						return acc;
					}, []) ?? [];
				state.orgs.data = [...orgsList, noOrg];
				state.orgs.error = null;
			})
			.addCase(StateActions.getAccountOrgs.pending, (state) => {
				state.orgs.pending = true;
				state.orgs.error = null;
			})
			.addCase(StateActions.getAccountOrgs.rejected, (state, action) => {
				state.orgs.pending = false;

				const message = action.error.message ?? 'unknown error';
				state.orgs.error = message;

				captureErrorWithContext({
					error: 'Error on fetching account orgs',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getMigrationPlanTotals.fulfilled, (state, { payload }) => {
				state.migrationPlanTotals.pending = false;
				state.migrationPlanTotals.data = payload;
				state.migrationPlanTotals.error = null;
			})
			.addCase(StateActions.getMigrationPlanTotals.pending, (state) => {
				state.migrationPlanTotals.pending = true;
				state.migrationPlanTotals.error = null;
			})
			.addCase(StateActions.getMigrationPlanTotals.rejected, (state) => {
				state.migrationPlanTotals.pending = false;
				state.migrationPlanTotals.data = {
					totalPlansCompleted: 0,
					totalPlansUnsuccessful: 0,
					totalPlansRunning: 0,
				};

				const message = 'error fetching migration plan totals';
				state.migrationPlanTotals.error = message;

				captureErrorWithContext({
					error: 'Error on fetching migration plan totals',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getL1Assessments.fulfilled, (state, { payload }) => {
				state.l1Assessments.pending = false;
				state.l1Assessments.data = payload;
				state.l1Assessments.error = null;
			})
			.addCase(StateActions.getL1Assessments.pending, (state) => {
				state.l1Assessments.pending = true;
				state.l1Assessments.error = null;
			})
			.addCase(StateActions.getL1Assessments.rejected, (state, action) => {
				state.l1Assessments.pending = false;
				state.l1Assessments.data = [];

				const message = action.error.message ?? 'unknown error';
				state.l1Assessments.error = message;

				captureErrorWithContext({
					error: 'Error on fetching l1 assessments',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getInstances.fulfilled, (state, { payload }) => {
				state.instances.pending = false;
				state.instances.data.instances = payload.instances;
				state.instances.data.totalCount = payload.totalCount;
				state.instances.error = null;
			})
			.addCase(StateActions.getInstances.pending, (state) => {
				state.instances.pending = true;
				state.instances.error = null;
			})
			.addCase(StateActions.getInstances.rejected, (state, action) => {
				state.instances.pending = false;
				state.instances.data = { instances: [], totalCount: 0 };

				const message = action.error.message ?? 'unknown error';
				state.instances.error = message;

				captureErrorWithContext({
					error: 'Error on fetching MMA instances',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getCustomerDomains.fulfilled, (state, { payload }) => {
				state.customerDomains.pending = false;
				state.customerDomains.data = payload;
				state.customerDomains.error = null;
			})
			.addCase(StateActions.getCustomerDomains.pending, (state) => {
				state.customerDomains.pending = true;
				state.customerDomains.error = null;
			})
			.addCase(StateActions.getCustomerDomains.rejected, (state, action) => {
				state.customerDomains.pending = false;
				state.customerDomains.data = {
					data: [],
					pagination: {
						page: 0,
						size: 20,
						totalElements: 0,
						totalPages: 0,
						hasNext: false,
						hasPrevious: false,
						sorted: false,
					},
				};

				const message = action.error.message ?? 'unknown error';
				state.customerDomains.error = message;

				captureErrorWithContext({
					error: 'Error on fetching customer domains list',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getCustomerDomain.fulfilled, (state, { payload }) => {
				state.customerDomain.pending = false;
				state.customerDomain.data = payload;
				state.customerDomain.error = null;
			})
			.addCase(StateActions.getCustomerDomain.pending, (state) => {
				state.customerDomain.pending = true;
				state.customerDomain.error = null;
			})
			.addCase(StateActions.getCustomerDomain.rejected, (state, action) => {
				state.customerDomain.pending = false;
				state.customerDomain.data = null;
				const message = action.error.message ?? 'unknown error';
				state.customerDomain.error = message;

				captureErrorWithContext({
					error: 'Error on fetching customer domain details',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getCustomerDomainEngagementDetails.fulfilled, (state, { payload }) => {
				state.customerDomainEngagement.pending = false;
				state.customerDomainEngagement.data = payload;
				state.customerDomainEngagement.error = null;
			})
			.addCase(StateActions.getCustomerDomainEngagementDetails.pending, (state) => {
				state.customerDomainEngagement.pending = true;
				state.customerDomainEngagement.error = null;
			})
			.addCase(StateActions.getCustomerDomainEngagementDetails.rejected, (state, action) => {
				state.customerDomainEngagement.pending = false;
				state.customerDomainEngagement.data = null;
				const message = action.error.message ?? 'unknown error';
				state.customerDomainEngagement.error = message;

				// check if error is expected
				const isExpectedError = Object.values(CustomerDomainEngagementExpectedError).includes(
					message as CustomerDomainEngagementExpectedError,
				);

				// if unexpected, capture on Sentry
				if (!isExpectedError) {
					captureErrorWithContext({
						error: 'Error on fetching customer domain engagement details',
						message,
						userDomain: getUserDomain(state),
					});
				}
			})
			// Legal Consent (Only required for Customer role)
			.addCase(StateActions.getPortalUserConsent.pending, (state) => {
				state.portalUserConsent.pending = true;
				state.portalUserConsent.error = null;
				state.portalUserConsent.data = null;
			})
			.addCase(StateActions.getPortalUserConsent.fulfilled, (state, { payload }) => {
				state.portalUserConsent.pending = false;
				state.portalUserConsent.data = payload;
				state.portalUserConsent.error = null;
			})
			.addCase(StateActions.getPortalUserConsent.rejected, (state, action) => {
				state.portalUserConsent.pending = false;
				state.portalUserConsent.data = null;
				const message = action.error.message ?? 'unknown error';
				state.portalUserConsent.error = message;

				// capture on Sentry
				captureErrorWithContext({
					error: 'Error on fetching user consent state',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.submitPortalUserConsent.pending, (state) => {
				state.portalUserConsent.pending = true;
				state.portalUserConsent.error = null;
			})
			.addCase(StateActions.submitPortalUserConsent.fulfilled, (state, { payload }) => {
				state.portalUserConsent.pending = false;
				state.portalUserConsent.data = payload;
				state.portalUserConsent.error = null;
			})
			.addCase(StateActions.submitPortalUserConsent.rejected, (state, action) => {
				state.portalUserConsent.pending = false;
				const message = action.error.message ?? 'unknown error';
				state.portalUserConsent.error = message;

				// capture on Sentry
				captureErrorWithContext({
					error: 'Error on submitting user consent state',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.setPortalUserConsent, (state, { payload }) => {
				state.portalUserConsent.data = payload;
			})
			// Fastshift support requests (M2-specific)
			.addCase(StateActions.getFastshiftSupportRequests.pending, (state) => {
				state.supportRequests.pending = true;
				state.supportRequests.error = null;
				state.supportRequests.data = null;
			})
			.addCase(StateActions.getFastshiftSupportRequests.fulfilled, (state, { payload, meta }) => {
				state.supportRequests.pending = false;
				state.supportRequests.data = payload.data;
				state.supportRequests.error = null;
				state.supportRequests.lastFetchParams = {
					customerDomainId: meta.arg.customerDomainId,
					queryParams: meta.arg.queryParams,
				};
			})
			.addCase(StateActions.getFastshiftSupportRequests.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				state.supportRequests.pending = false;
				state.supportRequests.data = null;
				const message = action.error.message ?? 'unknown error';

				state.supportRequests.error = message;

				captureErrorWithContext({
					error: 'Error on fetching support requests',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftTopSupportRequests.pending, (state) => {
				state.topSupportRequests.pending = true;
				state.topSupportRequests.error = null;
				state.topSupportRequests.data = null;
			})
			.addCase(
				StateActions.getFastshiftTopSupportRequests.fulfilled,
				(state, { payload, meta }) => {
					state.topSupportRequests.pending = false;
					state.topSupportRequests.data = payload;
					state.topSupportRequests.error = null;
					state.topSupportRequests.lastFetchParams = {
						customerDomainId: meta.arg.domainId,
						queryParams: meta.arg.queryParams,
					};
				},
			)
			.addCase(StateActions.getFastshiftTopSupportRequests.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				state.topSupportRequests.pending = false;
				state.topSupportRequests.data = null;
				const message = action.error.message ?? 'unknown error';
				state.topSupportRequests.error = message;

				captureErrorWithContext({
					error: 'Error on fetching top support requests',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.clearAllCustomerDomainScopedState, (state) => {
				state.customerDomain.pending = false;
				state.customerDomain.error = null;
				state.customerDomain.data = null;
				state.customerDomainEngagement.pending = false;
				state.customerDomainEngagement.error = null;
				state.customerDomainEngagement.data = null;
				state.selectedDomainOrg = null;
				state.pageStateCache[TABLE_IDS.MIGRATION_TICKETS] =
					initialState.pageStateCache[TABLE_IDS.MIGRATION_TICKETS];
				state.supportRequests.pending = false;
				state.supportRequests.error = null;
				state.supportRequests.data = null;
				state.supportRequests.lastFetchParams = undefined;
				state.topSupportRequests.pending = false;
				state.topSupportRequests.error = null;
				state.topSupportRequests.data = null;
				state.topSupportRequests.lastFetchParams = undefined;
				state.runbooks = initialState.runbooks;
			})
			.addCase(StateActions.clearCustomerDomain, (state) => {
				state.customerDomain.pending = false;
				state.customerDomain.data = null;
				state.customerDomain.error = null;
			})
			.addCase(StateActions.setTableControls, (state, { payload }) => {
				state.pageStateCache[payload.tableId] = payload.controls;
			})
			.addCase(StateActions.clearTableControls, (state, { payload }) => {
				state.pageStateCache[payload.tableId] = initialState.pageStateCache[payload.tableId];
			})
			.addCase(StateActions.getUserDetails.fulfilled, (state, { payload }) => {
				state.userDetails.pending = false;
				state.userDetails.data = payload;
				state.userDetails.error = null;
			})
			.addCase(StateActions.getUserDetails.pending, (state) => {
				state.userDetails.pending = true;
				state.userDetails.data = null;
				state.userDetails.error = null;
			})
			.addCase(StateActions.getUserDetails.rejected, (state, action) => {
				state.userDetails.pending = false;
				state.userDetails.data = null;

				const message = action.error.message ?? 'unknown error';
				state.userDetails.error = message;

				// check if error is expected
				const isExpectedError = Object.values(FastshiftUserDetailsExpectedError).includes(
					message as FastshiftUserDetailsExpectedError,
				);

				// if unexpected, capture on Sentry
				if (!isExpectedError) {
					captureErrorWithContext({
						error: 'Error on fetching user details',
						message,
					});
				}
			})
			.addCase(StateActions.getUserAccess.fulfilled, (state, { payload }) => {
				state.userAccess.pending = false;
				state.userAccess.data = payload;
				state.userAccess.error = null;
			})
			.addCase(StateActions.getUserAccess.pending, (state) => {
				state.userAccess.pending = true;
				state.userAccess.error = null;
			})
			.addCase(StateActions.getUserAccess.rejected, (state, action) => {
				state.userAccess.pending = false;
				state.userAccess.data = null;
				const message = action.error.message ?? 'unknown error';
				state.userAccess.error = message;

				// check if error is expected
				const isExpectedError = Object.values(FastshiftUserDetailsExpectedError).includes(
					message as FastshiftUserDetailsExpectedError,
				);

				// if unexpected, capture on Sentry
				if (!isExpectedError) {
					captureErrorWithContext({
						error: 'Error on fetching user access',
						message,
					});
				}
			})
			.addCase(StateActions.getFastshiftRunbooksBySupportTicketId.pending, (state, { meta }) => {
				const { supportTicketId } = meta.arg;
				state.runbooks.summariesBySupportTicketId[supportTicketId] = {
					pending: true,
					error: null,
					data: state.runbooks.summariesBySupportTicketId[supportTicketId]?.data ?? null,
				};
			})
			.addCase(
				StateActions.getFastshiftRunbooksBySupportTicketId.fulfilled,
				(state, { payload, meta }) => {
					state.runbooks.summariesBySupportTicketId[meta.arg.supportTicketId] = {
						pending: false,
						error: null,
						data: payload,
					};
				},
			)
			.addCase(StateActions.getFastshiftRunbooksBySupportTicketId.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				const { supportTicketId } = action.meta.arg;
				state.runbooks.summariesBySupportTicketId[supportTicketId] = {
					pending: false,
					data: state.runbooks.summariesBySupportTicketId[supportTicketId]?.data ?? null,
					error: action.error.message ?? 'unknown error',
				};

				captureErrorWithContext({
					error: 'Error on fetching runbooks by support ticket id',
					message: action.error.message ?? 'unknown error',
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.getFastshiftRunbook.pending, (state, { meta }) => {
				const { runbookId } = meta.arg;
				state.runbooks.detailsByRunbookId[runbookId] = {
					pending: true,
					error: null,
					data: state.runbooks.detailsByRunbookId[runbookId]?.data ?? null,
				};
			})
			.addCase(StateActions.getFastshiftRunbook.fulfilled, (state, { payload, meta }) => {
				state.runbooks.detailsByRunbookId[meta.arg.runbookId] = {
					pending: false,
					error: null,
					data: payload,
				};
			})
			.addCase(StateActions.getFastshiftRunbook.rejected, (state, action) => {
				if (action.error.name === StateTypes.FastshiftApiAbortError) {
					return;
				}

				const { runbookId } = action.meta.arg;
				state.runbooks.detailsByRunbookId[runbookId] = {
					pending: false,
					data: state.runbooks.detailsByRunbookId[runbookId]?.data ?? null,
					error: action.error.message ?? 'unknown error',
				};

				captureErrorWithContext({
					error: 'Error on fetching runbook',
					message: action.error.message ?? 'unknown error',
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepStatus.pending, (state, { meta }) => {
				setRunbookStepActionPending(state, meta.arg.runbookId, meta.arg.stepId);
			})
			.addCase(StateActions.updateFastshiftRunbookStepStatus.fulfilled, (state, { meta }) => {
				const { runbookId, stepId } = meta.arg;

				setRunbookStepActionSettled(state, runbookId, stepId, null);
				updateCachedRunbookStep(state, runbookId, stepId, {
					status: meta.arg.status,
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepStatus.rejected, (state, action) => {
				const { runbookId, stepId } = action.meta.arg;
				const message = action.error.message ?? 'unknown error';

				setRunbookStepActionSettled(state, runbookId, stepId, message);

				captureErrorWithContext({
					error: 'Error on updating runbook step status',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepRisk.pending, (state, { meta }) => {
				setRunbookStepActionPending(state, meta.arg.runbookId, meta.arg.stepId);
			})
			.addCase(StateActions.updateFastshiftRunbookStepRisk.fulfilled, (state, { meta }) => {
				const { runbookId, stepId } = meta.arg;

				setRunbookStepActionSettled(state, runbookId, stepId, null);
				updateCachedRunbookStep(state, runbookId, stepId, {
					risk: meta.arg.risk,
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepRisk.rejected, (state, action) => {
				const { runbookId, stepId } = action.meta.arg;
				const message = action.error.message ?? 'unknown error';

				setRunbookStepActionSettled(state, runbookId, stepId, message);

				captureErrorWithContext({
					error: 'Error on updating runbook step risk',
					message,
					userDomain: getUserDomain(state),
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepDisabled.pending, (state, { meta }) => {
				setRunbookStepActionPending(state, meta.arg.runbookId, meta.arg.stepId);
			})
			.addCase(StateActions.updateFastshiftRunbookStepDisabled.fulfilled, (state, { meta }) => {
				const { runbookId, stepId } = meta.arg;

				setRunbookStepActionSettled(state, runbookId, stepId, null);
				updateCachedRunbookStep(state, runbookId, stepId, {
					disabled: meta.arg.disabled,
				});
			})
			.addCase(StateActions.updateFastshiftRunbookStepDisabled.rejected, (state, action) => {
				const { runbookId, stepId } = action.meta.arg;
				const message = action.error.message ?? 'unknown error';

				setRunbookStepActionSettled(state, runbookId, stepId, message);

				captureErrorWithContext({
					error: 'Error on updating runbook step disabled state',
					message,
					userDomain: getUserDomain(state),
				});
			});
	});
